/**
 * FeatureSpotlightPage (#1876, part 2 of #1735) - every published product
 * update, newest first, at /updates. Reached from the user menu's Help
 * section (What's new). Same rows the login announcement shows, without
 * the seen/unseen split: this is the archive.
 */

import { useEffect, useState } from 'react';
import { Box, Container, Link as MuiLink, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiGlobal } from '../services/api';
import { useGuides } from '../user_guides/GuidesProvider.jsx';
import { CenteredLoader } from '../components/LoadingIndicator';
import { PageHero, SectionContainer } from '../components/ds';
import { formatPublished } from '../components/FeatureSpotlightDialog.jsx';

const FeatureSpotlightPage = ({ isAuthReady = false }) => {
  const guides = useGuides();
  const navigate = useNavigate();
  const [updates, setUpdates] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isAuthReady) return;
    let alive = true;
    apiGlobal.get('/tips')
      .then((res) => { if (alive) setUpdates(res?.updates || []); })
      .catch((err) => {
        console.warn('FeatureSpotlightPage: load failed', err);
        if (alive) { setFailed(true); setUpdates([]); }
      });
    return () => { alive = false; };
  }, [isAuthReady]);

  if (!isAuthReady || updates === null) return <CenteredLoader />;

  return (
    <Container maxWidth="md" disableGutters>
      <PageHero label="What's new" />
      <Box sx={{ mt: '13px' }}>
        <SectionContainer title="Updates" category="planned" count={updates.length}>
          {updates.length === 0 && (
            <Typography sx={{ color: 'text.secondary', fontSize: 14, py: 1 }}>
              {failed ? 'Updates could not be loaded.' : 'No updates yet.'}
            </Typography>
          )}
          <Stack spacing="14px" sx={{ pt: updates.length ? 1 : 0 }}>
            {updates.map((u) => (
              <Box key={u.updateId} data-testid={`updates-row-${u.updateId}`}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }}>
                  {u.title}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {formatPublished(u.publishedAt, guides.language)}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: '4px', whiteSpace: 'pre-line' }}>
                  {u.body}
                  {u.linkPath && (
                    <>
                      {' '}
                      <MuiLink
                        component="button"
                        type="button"
                        onClick={() => navigate(u.linkPath)}
                        sx={{ fontSize: '13px', color: 'interactive', verticalAlign: 'baseline' }}
                      >
                        Take me there
                      </MuiLink>
                    </>
                  )}
                </Typography>
              </Box>
            ))}
          </Stack>
        </SectionContainer>
      </Box>
    </Container>
  );
};

export default FeatureSpotlightPage;
